import AutocompleteInput, { Option } from '@/components/inputs/AutocompleteInput';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import LoaderBackdrop from '@/components/loaders/LoaderBackdrop';
import AmendmentConfirmModal from './AmendmentConfirmModal';
import { TextField, Alert, Chip } from '@mui/material';
import { GlobalButton } from '@/components';
import { useState } from 'react';
import {
  useAmendmentForm,
  REASON_MIN,
  REASON_MAX,
  type UseDriversAutocompleteHook,
} from '../../hooks/useAmendmentForm';

interface AmendmentFormProps {
  amendmentOptions: Option[];
  isAmendmentsLoading: boolean;
  onApply: (args: { transitionId: string; reason: string; driverId?: string }) => void;
  isApplying: boolean;
  currentStatusName: string;
  useDriversAutocompleteHook?: UseDriversAutocompleteHook;
}

const AmendmentForm = ({
  amendmentOptions,
  isAmendmentsLoading,
  onApply,
  isApplying,
  currentStatusName,
  useDriversAutocompleteHook,
}: AmendmentFormProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    amendment,
    selectedAmendment,
    isDeliveredStatus,
    filteredOptions,
    driverOptions,
    isDriversLoading,
    driverSelection,
    driverInput,
    reason,
    reasonSuggestions,
    isReasonSuggestionsLoading,
    reasonError,
    canApply,
    handleAmendmentChange,
    handleReasonChange,
    handleDriverChange,
    handleDriverInputChange,
    setSearchQuery,
    handleApply,
  } = useAmendmentForm({ amendmentOptions, onApply, useDriversAutocompleteHook });

  return (
    <div className='bg-white rounded-lg shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 p-8 flex flex-col gap-6'>
      <div className='flex flex-col gap-2'>
        <label className='text-[13px] font-bold text-gray-400 uppercase tracking-widest px-1'>
          Selecciona una enmienda
        </label>
        <AutocompleteInput
          name='amendment-selector'
          placeholder={isAmendmentsLoading ? 'Cargando enmiendas...' : 'Buscar enmienda...'}
          value={amendment}
          onChange={handleAmendmentChange}
          onInputChange={setSearchQuery}
          options={filteredOptions}
          loading={isAmendmentsLoading}
          showTypingLengthHint={false}
        />
        {!isAmendmentsLoading && amendmentOptions.length === 0 && (
          <Alert severity='warning'>
            No existen enmiendas disponibles para el estado actual del pedido.
          </Alert>
        )}
        {selectedAmendment && (
          <p className='text-sm text-gray-500 px-1'>
            Estado resultante:{' '}
            <strong className='text-gray-700'>{selectedAmendment.toStatus.commercialName}</strong>
          </p>
        )}
      </div>

      <div className='flex flex-col gap-2'>
        <label className='text-[13px] font-bold text-gray-400 uppercase tracking-widest px-1'>
          Motivo <span className='text-red-500'>*</span>
        </label>
        {!isReasonSuggestionsLoading && reason.length === 0 && reasonSuggestions.length > 0 && (
          <div className='flex flex-col gap-1.5 px-1'>
            <span className='text-xs text-gray-400'>Sugerencias frecuentes</span>
            <div className='flex flex-wrap gap-2'>
              {reasonSuggestions.slice(0, 5).map((suggestion) => (
                <Chip
                  key={suggestion.id}
                  label={suggestion.reason}
                  size='small'
                  variant='outlined'
                  onClick={() => handleReasonChange(suggestion.reason)}
                  sx={{
                    bgcolor: 'grey.50',
                    borderColor: 'grey.200',
                    color: 'grey.700',
                    '&:hover': { bgcolor: 'grey.100', borderColor: 'grey.300' },
                  }}
                />
              ))}
            </div>
          </div>
        )}
        <TextField
          multiline
          rows={4}
          placeholder='Ingresá el motivo de la enmienda (mínimo 10 caracteres)'
          value={reason}
          onChange={(e) => handleReasonChange(e.target.value)}
          error={reasonError}
          helperText={reasonError ? `Mínimo ${REASON_MIN} caracteres` : `${reason.length}/${REASON_MAX}`}
          inputProps={{ maxLength: REASON_MAX }}
          fullWidth
          variant='outlined'
        />
      </div>

      {isDeliveredStatus && (
        <div className='flex flex-col gap-2'>
          <label className='text-[13px] font-bold text-gray-400 uppercase tracking-widest px-1'>
            Conductor <span className='text-red-500'>*</span>
          </label>
          <AutocompleteInput
            name='amendment-driver-selector'
            placeholder='Buscar conductor'
            value={driverSelection}
            inputValue={driverSelection ? driverSelection.label : driverInput}
            onChange={handleDriverChange}
            onInputChange={handleDriverInputChange}
            options={driverOptions}
            loading={isDriversLoading}
          />
        </div>
      )}

      {isApplying && <LoaderBackdrop />}
      <div className='flex justify-end'>
        <GlobalButton
          variant={GlobalButtonVariant.primaryButton}
          action={() => setIsModalOpen(true)}
          disabled={!canApply || isApplying}
        >
          Aplicar enmienda
        </GlobalButton>
      </div>

      <AmendmentConfirmModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => {
          setIsModalOpen(false);
          handleApply();
        }}
        amendmentName={amendment?.label ?? ''}
        fromStatusName={currentStatusName}
        toStatusName={selectedAmendment?.toStatus?.commercialName ?? ''}
        reason={reason}
        driverName={driverSelection?.label}
      />
    </div>
  );
};

export default AmendmentForm;
